import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { X, Terminal, Filter, Trash, Pause, Play } from 'lucide-react';
import { socketStore } from "../../stores/socket.store";
import { serverStore } from "../../stores/server.store";
import { apiFetch } from "../../utils/api";

export const LogConsoleModal = observer(() => {
// Removed local isOpen
    const [logs, setLogs] = useState<string[]>([]);
    const [filter, setFilter] = useState('');
    const [autoScroll, setAutoScroll] = useState(true);
    const logsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!serverStore.showAdminLogs) return;

        // Fetch initial logs
        apiFetch('/api/system/logs')
            .then(r => r.json())
            .then(data => {
                if (data.logs) setLogs(data.logs);
            })
            .catch(console.error);

        const handleLog = (log: string) => {
            setLogs(prev => {
                const newLogs = [...prev, log];
                if (newLogs.length > 2000) newLogs.shift();
                return newLogs;
            });
        };

        if (socketStore.rawSocket) {
            socketStore.rawSocket.on('system:log', handleLog);
        }

        return () => {
             if (socketStore.rawSocket) {
                socketStore.rawSocket.off('system:log', handleLog);
             }
        };
    }, [serverStore.showAdminLogs, socketStore.rawSocket]);

    useEffect(() => {
        if (autoScroll && logsEndRef.current && serverStore.isLogConsoleOpen) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, autoScroll, serverStore.isLogConsoleOpen]);

    if (!serverStore.showAdminLogs) return null;

    if (!serverStore.isLogConsoleOpen) {
        return (
            <button 
                onClick={() => serverStore.setLogConsoleOpen(true)}
                className="fixed bottom-4 right-4 bg-ink border border-line text-white p-3 rounded-full shadow-lg hover:bg-surface-2 z-50 flex items-center justify-center transition-all hover:scale-110 group"
                title="Open Log Console"
            >
                <Terminal size={24} className="text-yellow-400 group-hover:text-yellow-300" />
                <span className="absolute right-full mr-2 bg-black text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-line">
                    Debug Console
                </span>
            </button>
        );
    }

    const filteredLogs = logs.filter(l => l.toLowerCase().includes(filter.toLowerCase()));

    return (
        <div className="fixed inset-x-0 bottom-0 h-96 bg-ink border-t border-line shadow-[0_-5px_20px_rgba(0,0,0,0.5)] z-50 flex flex-col transition-transform duration-300 transform translate-y-0">
            {/* Header */}
            <div className="flex items-center justify-between p-2 bg-ink/95 border-b border-line">
                <div className="flex items-center space-x-3">
                    <Terminal size={18} className="text-yellow-400 ml-2" />
                    <span className="font-bold text-fg text-sm">System Logs (Debug)</span>
                    <span className="text-[10px] text-fg-muted bg-surface-2 px-1.5 py-0.5 rounded font-mono">{filteredLogs.length} events</span>
                </div>
                <div className="flex items-center space-x-2">
                     <div className="relative">
                        <Filter size={14} className="absolute left-2 top-1.5 text-fg-muted" />
                        <input 
                            type="text" 
                            placeholder="Filter..." 
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="bg-surface-2 border border-line text-fg text-xs rounded pl-7 pr-2 py-1 focus:border-blue-500 outline-none w-40 hover:bg-gray-700 transition-colors"
                        />
                    </div>
                    <div className="h-4 w-px bg-gray-700 mx-1"></div>
                    <button onClick={() => setAutoScroll(!autoScroll)} className={`p-1.5 rounded hover:bg-gray-700 transition-colors ${autoScroll ? 'text-green-400 bg-green-900/20' : 'text-fg-muted'}`} title="Auto Scroll">
                        {autoScroll ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <button onClick={() => setLogs([])} className="p-1.5 rounded hover:bg-red-900/30 text-fg-muted hover:text-red-400 transition-colors" title="Clear View">
                        <Trash size={16} />
                    </button>
                     <div className="h-4 w-px bg-gray-700 mx-1"></div>
                    <button onClick={() => serverStore.setLogConsoleOpen(false)} className="p-1.5 rounded hover:bg-gray-700 text-fg-muted hover:text-white transition-colors">
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Log Content */}
            <div className="flex-1 overflow-y-auto p-4 font-mono text-[11px] leading-relaxed space-y-0.5 bg-ink text-fg selection:bg-yellow-900/50">
                {filteredLogs.map((log, i) => {
                    const isError = log.includes('[ERROR]');
                    const isInfo = log.includes('[INFO]');
                    return (
                        <div key={i} className={`break-words hover:bg-white/5 px-1 rounded ${isError ? 'text-red-400' : isInfo ? 'text-blue-300' : 'text-fg'}`}>
                            {log}
                        </div>
                    );
                })}
                {filteredLogs.length === 0 && (
                    <div className="text-fg-muted italic text-center mt-10">Waiting for logs...</div>
                )}
                <div ref={logsEndRef} />
            </div>
        </div>
    );
});
